import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import {
    ICompanySignaturePatch, ICompanySignatureResponse, ICrmError, ISalesAccess, ISendingProfilePatch, ISignaturePreview, ISignaturePreviewInput, ISignatureStats
} from "./types";

const crmUrl = `${environment.v2Url}/crm`;

export type CrmSignatureFunctions = {
    /** The company signature design, whether the caller may edit it, the brand kit logo and accent. */
    getCompany: () => Promise<ICompanySignatureResponse | ICrmError>;
    /** Managers only. Send only what changed. */
    updateCompany: (patch: ICompanySignaturePatch) => Promise<ICompanySignatureResponse | ICrmError>;
    /** The caller's signature rendered from drafts; omitted parts render as saved. */
    preview: (input: ISignaturePreviewInput) => Promise<ISignaturePreview | ICrmError>;
    /** Managers only: clicks on signature links over the last 7, 30 or 90 days. */
    stats: (days: number) => Promise<ISignatureStats | ICrmError>;
    /** PUT /crm/me with the rep's signature fields; answers with the access summary. */
    updateMe: (patch: ISendingProfilePatch) => Promise<ISalesAccess | ICrmError>;
    uploadPhoto: (file: File) => Promise<ISalesAccess | ICrmError>;
    /** Uses their current CRM/Google photo. */
    photoFromAvatar: () => Promise<ISalesAccess | ICrmError>;
    removePhoto: () => Promise<ISalesAccess | ICrmError>;
};

// #region useCrmSignature
/** Email signatures: the company design (managers), the rep's own fields and photo, preview and click stats. Never throws. */
export const useCrmSignature = (): [CrmSignatureFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        const raw = String(err?.response?.data?.message ?? '');
        return {
            ok: false,
            status,
            message: raw.replace(/^\[[^\]]+\](\[[^\]]+\])?:\s*/, '') || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
        } as ICrmError;
    };
    const call = async <T,>(fn: () => Promise<AxiosResponse<T>>): Promise<T | ICrmError> => {
        try { return (await fn()).data; } catch (err: any) { return handle(err); }
    };

    return [{
        getCompany: () => call(() => axios.get(`${crmUrl}/signature`, { headers })),
        updateCompany: (patch) => call(() => axios.put(`${crmUrl}/signature`, patch, { headers })),
        preview: (input) => call(() => axios.post(`${crmUrl}/signature/preview`, input, { headers })),
        stats: (days) => call(() => axios.get(`${crmUrl}/signature/stats`, { headers, params: { days } })),
        updateMe: (patch) => call(() => axios.put(`${crmUrl}/me`, patch, { headers })),
        uploadPhoto: (file) => {
            const form = new FormData();
            form.append('file', file);
            return call(() => axios.post(`${crmUrl}/me/signature-photo`, form, { headers }));
        },
        photoFromAvatar: () => call(() => axios.post(`${crmUrl}/me/signature-photo/from-avatar`, {}, { headers })),
        removePhoto: () => call(() => axios.delete(`${crmUrl}/me/signature-photo`, { headers }))
    }];
};
